import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CompanyModal from './CompanyModal';
import StandardHeader from './layout/StandardHeader';
import { Plus, ChevronLeft } from 'lucide-react';
import CompanyStats from './empresas/CompanyStats';
import ClientFilters from './empresas/ClientFilters';
import ClientTable from './empresas/ClientTable';
import CompanyActivity from './empresas/CompanyActivity';
import UpcomingEvents from './empresas/UpcomingEvents';
import FinancialSummary from './empresas/FinancialSummary';
import Charts from './empresas/Charts';
import CompanyActionBar from './empresas/CompanyActionBar';
import { useNavigation } from '../context/NavigationContext';
import { Vision360 } from './common/Vision360';
import { EntityHero } from './common/EntityHero';
import { apiClient } from '../api/client';
import LoadingState from './ui/LoadingState';
import ErrorState from './ui/ErrorState';
import EmptyState from './ui/EmptyState';

const enrichCompany = (c: any, projectsData: any[]) => {
  const pCount = projectsData.filter((p: any) => p.companyId === c.id).length;

  let responsibleName = 'Equipe Comercial';
  let responsibleRole = 'Relacionamento';
  let lastContactDays = 3;
  let financialStatus = 'Em dia';
  let status = c.status || 'Ativo';
  let email = `contato@${(c.name || 'empresa').toLowerCase().replace(/[^a-z0-9]/g, '')}.com.br`;

  if (c.id === 1 || c.id === '1') {
    responsibleName = 'João Silva';
    responsibleRole = 'Account Manager';
    lastContactDays = 0;
    financialStatus = 'Em dia';
    status = 'Ativo';
    email = 'suporte@techsolutions.com';
  } else if (c.id === 2 || c.id === '2') {
    responsibleName = 'Maria Souza';
    responsibleRole = 'SDR / Leads';
    lastContactDays = 3;
    financialStatus = 'R$ 2.350 em aberto';
    status = 'Lead';
    email = 'vendas@varejoexpress.com';
  } else if (c.id === 3 || c.id === '3') {
    responsibleName = 'Equipe Comercial';
    responsibleRole = 'Key Accounts';
    lastContactDays = 15;
    financialStatus = 'Pagamento vence amanhã';
    status = 'Cliente VIP';
    email = 'contact@globalfinance.com';
  } else if (c.id === 4 || c.id === '4') {
    responsibleName = 'Mateus Reis';
    responsibleRole = 'Comercial Sênior';
    lastContactDays = 1;
    financialStatus = 'Em dia';
    status = 'Negociação';
    email = 'contato@alphalogistica.com.br';
  } else if (c.id === 5 || c.id === '5') {
    responsibleName = 'Ana Paula';
    responsibleRole = 'Customer Success';
    lastContactDays = 28;
    financialStatus = 'R$ 4.200 atrasado';
    status = 'Em risco';
    email = 'suporte@omegasolucoes.com';
  } else if (c.id === 6 || c.id === '6') {
    responsibleName = 'Carlos Eduardo';
    responsibleRole = 'Diretor de Operações';
    lastContactDays = 45;
    financialStatus = 'Em dia';
    status = 'Inativo';
    email = 'diretoria@sigmaconsultoria.com';
  } else {
    const numId = typeof c.id === 'number' ? c.id : parseInt(c.id) || 10;
    const managers = ['João Silva', 'Maria Souza', 'Mateus Reis', 'Ana Paula', 'Carlos Eduardo'];
    const roles = ['Account Manager', 'CSM', 'Comercial', 'Customer Success', 'Diretoria'];
    const contacts = [0, 1, 4, 12, 30, 48];
    const finances = ['Em dia', 'R$ 1.200 em aberto', 'Pagamento vence amanhã', 'Em dia', 'R$ 3.500 atrasado'];
    const statuses = ['Ativo', 'Lead', 'Negociação', 'Cliente VIP', 'Em risco', 'Inativo'];

    responsibleName = managers[numId % managers.length];
    responsibleRole = roles[numId % roles.length];
    lastContactDays = contacts[numId % contacts.length];
    financialStatus = finances[numId % finances.length];
    status = statuses[numId % statuses.length];
  }

  return {
    ...c,
    projects: pCount,
    responsibleName,
    responsibleRole,
    lastContactDays,
    financialStatus,
    status,
    email,
  };
};

export default function EmpresasView() {
  const { globalFilters, setGlobalFilters } = useNavigation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [segmentFilter, setSegmentFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [sortField, setSortField] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const { data: companies, isLoading: companiesLoading, error: companiesError, refetch: refetchCompanies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => apiClient.get('/companies').then((r) => r.data),
  });

  const { data: projects, refetch: refetchProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiClient.get('/projects').then((r) => r.data),
  });

  const { data: clients, refetch: refetchClients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => apiClient.get('/customers').then((r) => r.data),
  });

  const { data: invoices, refetch: refetchInvoices } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => apiClient.get('/invoices').then((r) => r.data),
  });

  const createCompanyMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/companies', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      refetchCompanies();
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => apiClient.put(`/companies/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      refetchCompanies();
    },
  });

  const deleteCompanyMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/companies/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      refetchCompanies();
    },
  });

  const enrichedCompanies = useMemo(() => {
    if (!companies) return [];
    return companies.map((c: any) => enrichCompany(c, projects || []));
  }, [companies, projects]);

  const filteredCompanies = useMemo(() => {
    return enrichedCompanies.filter((c: any) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        c.name?.toLowerCase().includes(term) ||
        c.industry?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.responsibleName?.toLowerCase().includes(term);

      let matchesStatus = true;
      if (statusFilter !== 'ALL') {
        const s = c.status?.toUpperCase() || 'ATIVO';
        if (statusFilter === 'CLIENTES') {
          matchesStatus = s === 'ATIVO' || s === 'ACTIVE' || s === 'CLIENTE VIP' || s === 'VIP' || s === 'EM RISCO' || s === 'AT_RISK';
        } else if (statusFilter === 'LEADS') {
          matchesStatus = s === 'LEAD';
        } else if (statusFilter === 'VIP') {
          matchesStatus = s === 'VIP' || s === 'CLIENTE VIP';
        } else if (statusFilter === 'NEGOCIACAO') {
          matchesStatus = s === 'NEGOCIAÇÃO' || s === 'NEGOCIACAO' || s === 'NEGOTIATION';
        } else if (statusFilter === 'INACTIVE') {
          matchesStatus = s === 'INATIVO' || s === 'INACTIVE';
        }
      }

      let matchesSegment = true;
      if (segmentFilter !== 'ALL') {
        const seg = c.industry?.toUpperCase() || '';
        matchesSegment = seg.includes(segmentFilter.toUpperCase());
      }

      return matchesSearch && matchesStatus && matchesSegment;
    });
  }, [enrichedCompanies, searchTerm, statusFilter, segmentFilter]);

  const sortedCompanies = useMemo(() => {
    return [...filteredCompanies].sort((a: any, b: any) => {
      let valA = a[sortField];
      let valB = b[sortField];
      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();
      if (valA === undefined) valA = '';
      if (valB === undefined) valB = '';
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCompanies, sortField, sortOrder]);

  const handleEditClick = (company: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditingCompany(company);
    setIsModalOpen(true);
  };

  const handleOpenModal = (company: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditingCompany(company);
    setIsModalOpen(true);
  };

  const handleNavigateCompany = (company: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setGlobalFilters({ ...globalFilters, companyId: company.id.toString() });
  };

  const toggleSelection = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCompanyIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearSelection = () => {
    setSelectedCompanyIds([]);
  };

  const activeCompaniesCount = enrichedCompanies.filter((c) => c.status === 'Ativo' || c.status === 'ACTIVE' || !c.status).length;
  const totalProjectsCount = enrichedCompanies.reduce((acc, c) => acc + Number(c.projects || 0), 0);
  const totalRevenue = invoices?.filter((inv: any) => inv.status === 'PAID').reduce((sum: number, inv: any) => sum + Number(inv.total || 0), 0) || 0;

  if (companiesLoading) {
    return <LoadingState message="Carregando empresas..." />;
  }

  if (companiesError) {
    return <ErrorState message="Erro ao carregar empresas" retry={() => refetchCompanies()} />;
  }

  if (globalFilters.companyId) {
    const activeCompany = enrichedCompanies.find((c) => c.id.toString() === globalFilters.companyId?.toString());
    if (activeCompany) {
      return (
        <div className="w-full mx-auto pb-12 flex flex-col animate-in fade-in duration-500 relative bg-[#FAFAFA]/30">
          <div className="flex flex-col">
            <EntityHero
              entityType="company"
              name={activeCompany.name}
              description={activeCompany.industry ? `Empresa atuante no segmento de ${activeCompany.industry}.` : 'Parceiro comercial registrado no ecossistema Cyzor Control.'}
              logoUrl={activeCompany.logoUrl}
              coverUrl={activeCompany.coverUrl}
              breadcrumbs={['Perspectiva Corporativa', '360°', activeCompany.name]}
              badges={[
                { label: activeCompany.status || 'Ativo', variant: 'secondary' },
                { label: activeCompany.industry || 'Sem Setor', variant: 'neutral' },
                { label: activeCompany.cnpj || 'Sem CNPJ', variant: 'neutral' },
              ]}
              actions={
                <button onClick={() => setGlobalFilters({})} className="px-4 py-2 rounded-xl bg-white/10 text-white/90 hover:bg-white/20 hover:text-white flex items-center gap-2 transition-all cursor-pointer font-bold text-xs">
                  <ChevronLeft size={16} />
                  <span>Voltar para Lista</span>
                </button>
              }
            />
          </div>
          <div className="px-4 sm:px-6 lg:px-10 mt-6">
            <Vision360 entityType="company" entityId={activeCompany.id} entityName={activeCompany.name} entityData={activeCompany} />
          </div>
        </div>
      );
    }
  }

  return (
    <div className="w-full mx-auto pb-12 flex flex-col gap-10 animate-in fade-in duration-500 relative px-4 sm:px-6 lg:px-10">
      <StandardHeader
        title="Empresas"
        subtitle="Gerencie todas as empresas, projetos, clientes e indicadores em um único lugar."
        actions={[
          {
            label: 'Nova Empresa',
            icon: Plus,
            onClick: () => {
              setEditingCompany(null);
              setIsModalOpen(true);
            },
            variant: 'primary',
          },
        ]}
      />

      <CompanyStats
        totalCompanies={enrichedCompanies.length}
        activeCompanies={activeCompaniesCount}
        totalRevenue={totalRevenue}
        totalProjects={totalProjectsCount}
        totalClients={clients?.length || 0}
      />

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-6">
          <ClientFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            segmentFilter={segmentFilter}
            setSegmentFilter={setSegmentFilter}
            viewMode={viewMode}
            setViewMode={setViewMode}
            sortField={sortField}
            setSortField={setSortField}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
          />
          {sortedCompanies.length === 0 ? (
            <EmptyState title="Nenhuma empresa encontrada" description="Cadastre uma nova empresa para começar." onCreate={() => setIsModalOpen(true)} />
          ) : (
            <ClientTable
              companies={sortedCompanies}
              finance={invoices || []}
              onSelect={handleNavigateCompany}
              onEdit={handleEditClick}
              onViewModal={handleOpenModal}
              onNavigate={handleNavigateCompany}
              selectedCompanyIds={selectedCompanyIds}
              toggleSelection={toggleSelection}
              viewMode={viewMode}
              sortField={sortField}
              onSort={(field) => {
                if (sortField === field) {
                  setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                } else {
                  setSortField(field);
                  setSortOrder('asc');
                }
              }}
              sortOrder={sortOrder}
            />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9">
            <Charts finance={invoices || []} companies={enrichedCompanies} />
          </div>
          <div className="lg:col-span-3">
            <CompanyActivity />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <UpcomingEvents />
          <FinancialSummary />
        </div>
      </div>

      <CompanyActionBar selectedCount={selectedCompanyIds.length} onClear={clearSelection} />

      <CompanyModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCompany(null);
        }}
        onSuccess={() => {
          refetchCompanies();
          refetchProjects();
          refetchInvoices();
        }}
        company={editingCompany}
      />
    </div>
  );
}
